import React, { useState, useEffect } from 'react';
import { Text, Flex } from '@chakra-ui/react';
import ReactECharts from 'echarts-for-react';
import ChartCard from './ChartCard';
import { metricsService } from '../../services/metricsService';
import type { DealSentimentSummary } from '../../services/metricsService';

interface LeadTemperatureChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const LeadTemperatureChart: React.FC<LeadTemperatureChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<DealSentimentSummary | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await metricsService.getDealSentimentSummary(
          businessIdentifier,
          timeRange || '7d'
        );
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch lead temperature data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (!data) return {};

    const hot = data.positive_sentiment_count;
    const warm = data.neutral_sentiment_count;
    const cold = data.negative_sentiment_count;
    const total = hot + warm + cold;

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0.0';
          return `${params.name}: ${params.value} (${percent}%)`;
        },
        textStyle: {
          fontFamily: FONT_FAMILY,
        },
      },
      legend: {
        bottom: 0,
        left: 'center',
        textStyle: {
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          color: '#718096',
        },
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20,
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '38%',
          style: {
            text: String(total),
            textAlign: 'center',
            fill: '#2D3748',
            fontSize: 28,
            fontWeight: 700,
            fontFamily: FONT_FAMILY,
          },
        },
        {
          type: 'text',
          left: 'center',
          top: '50%',
          style: {
            text: 'Total Leads',
            textAlign: 'center',
            fill: '#A0AEC0',
            fontSize: 12,
            fontFamily: FONT_FAMILY,
          },
        },
      ],
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: '{b}: {c}',
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            color: '#4A5568',
          },
          labelLine: {
            show: true,
            length: 10,
            length2: 15,
          },
          data: [
            {
              value: hot,
              name: 'Hot',
              itemStyle: { color: '#FF6B6B' },
            },
            {
              value: warm,
              name: 'Warm',
              itemStyle: { color: '#FFD93D' },
            },
            {
              value: cold,
              name: 'Cold',
              itemStyle: { color: '#4A90D9' },
            },
          ],
        },
      ],
    };
  };

  const hasData =
    data &&
    (data.positive_sentiment_count > 0 ||
      data.neutral_sentiment_count > 0 ||
      data.negative_sentiment_count > 0);

  return (
    <ChartCard title="Lead Temperature Distribution" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '300px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            No data available
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default LeadTemperatureChart;
